import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

import GridWrapper from '../../shared/components/layout/GridWrapper';
import BuilderLayout from '../components/layout/AppLayout';
import SuperAdminLayout from '../../super-admin/components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import FormInput from '../../shared/components/ui/FormInput';

import company_image from '../../shared/assets/png/company_image.png';
import {
  getMyData,
  getProjectsByCompanyId,
  getMySmtpStatus,
  saveMySmtp,
  deleteMySmtp
} from '../services/api/implementation/impl';
import {
  getCognitoStatus,
  resendInvitation,
  getActiveSessions,
  revokeActiveSession,
  logoutOtherSessions
} from '../../shared/services/api/implementation/impl';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { getFixedHeaderClass } from '../../shared/utils/sidebarLayout';
import Cookies from 'js-cookie';
import { useLocation } from 'react-router-dom';
import { useSmartBack } from '../../shared/navigation';

function formatSessionTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return String(value);
  }
}

const Profile = () => {
  const currentUserId = Cookies.get('userId');
  const { collapsed } = useSidebar();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isSuperAdminProfile =
    location.pathname.startsWith('/super-admin') || Cookies.get('userRole') === 'admin';
  const Layout = isSuperAdminProfile ? SuperAdminLayout : BuilderLayout;
  const { onBack } = useSmartBack({
    fallback: isSuperAdminProfile ? '/super-admin/home' : '/builder/dashboard'
  });

  const [smtpModalOpen, setSmtpModalOpen] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['myProfile'],
    queryFn: getMyData,
    enabled: !!currentUserId
  });

  const { data: smtpStatus, isLoading: smtpStatusLoading } = useQuery({
    queryKey: ['mySmtpStatus'],
    queryFn: getMySmtpStatus,
    enabled: !!currentUserId && !isSuperAdminProfile
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      smtp_host: 'smtp.gmail.com',
      smtp_port: 465,
      smtp_secure: true,
      smtp_user: '',
      smtp_password: '',
      smtp_from_email: '',
      smtp_from_name: ''
    }
  });

  // Same as EditUser: derive selected companies from user's company mapping
  const selectedCompanies =
    user?.userCompanyMapping
      ?.map((m) => ({
        id: m?.company?.id,
        name: m?.company?.company_name?.trim() || m?.company?.name
      }))
      ?.filter((c) => c?.id != null) || [];

  // Fetch projects for assigned companies (same as EditUser: getProjectsByCompanyId per company)
  const { data: companyProjects = [] } = useQuery({
    queryKey: ['profileProjects', selectedCompanies?.map((c) => c.id)],
    queryFn: async () => {
      if (!selectedCompanies?.length) return [];
      try {
        const projectPromises = selectedCompanies.map(async (company) => {
          const projects = await getProjectsByCompanyId(company.id);
          return projects.map((project) => ({
            ...project,
            company_name: company.name
          }));
        });
        const projectsArrays = await Promise.all(projectPromises);
        return projectsArrays.flat();
      } catch (error) {
        console.error('Error fetching projects for profile:', error);
        return [];
      }
    },
    enabled: !!selectedCompanies?.length
  });

  const transformedDataProjects =
    companyProjects?.map((item) => ({
      name: item.name || '',
      id: item.id,
      displayName: `${item.name || ''} (${item.company_name || ''})`,
      company_id: item.company_id
    })) || [];

  const { data: cognitoStatus } = useQuery({
    queryKey: ['cognitoStatus', user?.email],
    queryFn: () => getCognitoStatus({ email: user?.email }),
    enabled: !!user?.email
  });

  const {
    data: sessions = [],
    isLoading: sessionsLoading,
    isError: sessionsError
  } = useQuery({
    queryKey: ['activeSessions'],
    queryFn: getActiveSessions,
    enabled: !!currentUserId
  });

  const headerData = [
    { name: 'Phone Number', value: user?.phone_number },
    { name: 'Email Address', value: user?.email },
    { name: 'Vertical', value: user?.designation?.vertical ?? user?.vertical },
    {
      name: 'Designation Level',
      value:
        user?.level === 'Low'
          ? 'Executive Level'
          : user?.level === 'Medium'
            ? 'Manager Level'
            : 'Admin Level'
    },
    { name: 'Designation', value: user?.designation?.name }
  ];

  const assignedCompanies =
    selectedCompanies?.map((c) => c?.name).filter(Boolean) ||
    user?.userCompanyMapping
      ?.map((m) => m?.company?.company_name || m?.company?.name)
      ?.filter(Boolean) ||
    [];
  const assignedProjectIds =
    user?.userProjectMapping?.map((m) => m?.project?.id ?? m?.project_id)?.filter(Boolean) || [];
  const assignedProjects = assignedProjectIds.map((projectId) => {
    const found = transformedDataProjects.find(
      (p) => p.id === projectId || p.id === Number(projectId)
    );
    return found
      ? found.displayName
      : user?.userProjectMapping?.find(
          (m) => (m?.project?.id ?? m?.project_id) === projectId
        )?.project?.name || String(projectId);
  });

  const { mutate: handleSetPasswordInvite, isLoading: isSendingInvite } = useMutation({
    mutationFn: async (email) => resendInvitation({ email }),
    onSuccess: () => {
      toast.success('Set password invitation sent to your email.');
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to send set password invitation.'
      );
    }
  });

  const { mutate: handleRevokeSession, isLoading: isRevoking } = useMutation({
    mutationFn: (sessionId) => revokeActiveSession(sessionId),
    onSuccess: () => {
      toast.success('Device logged out.');
      queryClient.invalidateQueries({ queryKey: ['activeSessions'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to log out device.');
    }
  });

  const { mutate: handleLogoutOthers, isLoading: isLoggingOutOthers } = useMutation({
    mutationFn: () => logoutOtherSessions(),
    onSuccess: () => {
      toast.success('Logged out of other devices.');
      queryClient.invalidateQueries({ queryKey: ['activeSessions'] });
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to log out other devices.'
      );
    }
  });

  const { mutate: saveSmtp, isLoading: isSavingSmtp } = useMutation({
    mutationFn: (payload) => saveMySmtp(payload),
    onSuccess: () => {
      toast.success('Gmail connected for AI Insights emails');
      setSmtpModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['mySmtpStatus'] });
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to connect Gmail'
      );
    }
  });

  const { mutate: disconnectSmtp, isLoading: isDisconnectingSmtp } = useMutation({
    mutationFn: () => deleteMySmtp(),
    onSuccess: () => {
      toast.success('Gmail disconnected');
      queryClient.invalidateQueries({ queryKey: ['mySmtpStatus'] });
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to disconnect Gmail'
      );
    }
  });

  const handleSetPassword = () => {
    if (!user?.email) {
      toast.error('Email not found for this account.');
      return;
    }
    if (!cognitoStatus?.canResendInvitation) {
      toast.error('Set password is not available for this account.');
      return;
    }
    handleSetPasswordInvite(user.email);
  };

  const otherSessionsCount = sessions.filter((s) => !s.is_current).length;

  const openSmtpModal = () => {
    reset({
      smtp_host: 'smtp.gmail.com',
      smtp_port: 465,
      smtp_secure: true,
      smtp_user: user?.email || '',
      smtp_password: '',
      smtp_from_email: user?.email || '',
      smtp_from_name: user?.name || ''
    });
    setSmtpModalOpen(true);
  };

  const onSmtpSubmit = (data) => {
    saveSmtp({
      ...data,
      smtp_host: data.smtp_host || 'smtp.gmail.com',
      smtp_port: Number(data.smtp_port) || 465,
      smtp_secure: data.smtp_secure !== false && data.smtp_secure !== 'false'
    });
  };

  return (
    <Layout>
      <div className={getFixedHeaderClass(collapsed)}>
        <Header data={{ name: 'Profile' }} backArrow={true} onBack={onBack} />
      </div>
      <GridWrapper width={100}>
        <div className="w-full flex flex-col justify-between items-center gap-8 mt-[6rem]">
          <div className="p-8 rounded-lg bg-white w-full shadow-md flex items-center justify-between gap-6 space-x-10">
            {user?.name ? (
              <div className="bg-gray-300 w-[12rem] h-[12rem] rounded-full flex justify-center items-center">
                <span className="text-[8rem] text-white bg-center bg-cover !object-cover !object-center">
                  {user.name[0]}
                </span>
              </div>
            ) : (
              <img
                src={company_image}
                alt="company_image"
                className="w-[12rem] bg-center bg-cover !object-cover !object-center"
              />
            )}

            <div className="flex-1 flex flex-col gap-8">
              <div className="flex justify-between items-start">
                <div className="flex flex-col gap-4">
                  <span className="font-semibold text-xl">{user?.name}</span>
                  <span className="text-gray-400 text-sm">{user?.user_type}</span>
                </div>
                {cognitoStatus?.canResendInvitation && (
                  <Button type="button" onClick={handleSetPassword} disabled={isSendingInvite}>
                    Set Password
                  </Button>
                )}
              </div>

              <div className="w-[100%] flex justify-between items-center gap-2 flex-wrap">
                {headerData?.map((item, index) => (
                  <div className="flex flex-col gap-4 flex-shrink-0 min-w-[15%]" key={index}>
                    <span>{item.name}</span>
                    <span className="text-sm">{item.value}</span>
                  </div>
                ))}
              </div>

              <div className="w-full flex flex-col gap-5 mt-2">
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-gray-600">Assigned Companies</span>
                  <div className="flex flex-wrap gap-2">
                    {assignedCompanies?.length ? (
                      assignedCompanies.filter(Boolean).map((name, idx) => (
                        <span
                          key={`company-${idx}`}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-[#2A565E]/10 text-[#2A565E] border border-[#2A565E]/20">
                          {name}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-gray-600">Assigned Projects</span>
                  <div className="flex flex-wrap gap-2">
                    {assignedProjects?.length ? (
                      assignedProjects.filter(Boolean).map((name, idx) => (
                        <span
                          key={`project-${idx}`}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-[#2A565E]/10 text-[#2A565E] border border-[#2A565E]/20">
                          {name}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!isSuperAdminProfile && (
            <div className="p-6 rounded-lg bg-white w-full shadow-md flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-lg text-gray-900">
                  My email (AI Insights)
                </span>
                <p className="text-sm text-gray-500">
                  Connect your Gmail so AI Insights emails are sent from your inbox. Use a Gmail
                  App Password (Google Account → Security → App passwords).
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  {smtpStatusLoading ? (
                    <span className="text-gray-400">Checking connection…</span>
                  ) : smtpStatus?.connected ? (
                    <span className="text-teal-800">
                      Connected as{' '}
                      <strong>{smtpStatus.fromEmailMasked || smtpStatus.fromEmail}</strong>
                      {smtpStatus.fromName ? ` (${smtpStatus.fromName})` : ''}
                    </span>
                  ) : (
                    <span className="text-amber-700">Not connected — AI email send is blocked</span>
                  )}
                </div>
                <div className="flex gap-2">
                  {smtpStatus?.connected && (
                    <Button
                      type="button"
                      displayType="secondary"
                      disabled={isDisconnectingSmtp}
                      onClick={() => disconnectSmtp()}>
                      {isDisconnectingSmtp ? 'Disconnecting…' : 'Disconnect'}
                    </Button>
                  )}
                  <Button type="button" onClick={openSmtpModal} disabled={isSavingSmtp}>
                    {smtpStatus?.connected ? 'Update Gmail' : 'Connect Gmail'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="p-8 rounded-lg bg-white w-full shadow-md flex flex-col gap-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-xl">Active sessions</span>
                <span className="text-sm text-gray-500">
                  Devices where you are currently logged in. You can log out a device remotely.
                </span>
              </div>
              {otherSessionsCount > 0 && (
                <Button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        'Log out of all other devices? This device will stay signed in.'
                      )
                    ) {
                      handleLogoutOthers();
                    }
                  }}
                  disabled={isLoggingOutOthers}>
                  Log out other devices
                </Button>
              )}
            </div>

            {sessionsLoading ? (
              <span className="text-sm text-gray-400">Loading sessions…</span>
            ) : sessionsError ? (
              <span className="text-sm text-red-500">
                Unable to load sessions. Try refreshing the page.
              </span>
            ) : !sessions.length ? (
              <span className="text-sm text-gray-400">
                No tracked sessions yet. Sign in again on your devices to see them here.
              </span>
            ) : (
              <div className="flex flex-col divide-y divide-gray-100">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex flex-wrap items-center justify-between gap-4 py-4 first:pt-0 last:pb-0">
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-[#2A565E]">
                          {session.device_label || 'Unknown device'}
                        </span>
                        {session.is_current && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#2A565E]/10 text-[#2A565E] border border-[#2A565E]/20">
                            This device
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {session.ip_address ? `IP ${session.ip_address} · ` : ''}
                        Last active {formatSessionTime(session.last_seen_at)}
                      </span>
                    </div>
                    {!session.is_current && (
                      <button
                        type="button"
                        className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                        disabled={isRevoking}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Log out of ${session.device_label || 'this device'}?`
                            )
                          ) {
                            handleRevokeSession(session.id);
                          }
                        }}>
                        Log out
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </GridWrapper>

      {smtpModalOpen && (
        <Modal
          name={smtpStatus?.connected ? 'Update Gmail SMTP' : 'Connect Gmail SMTP'}
          onClose={() => !isSavingSmtp && setSmtpModalOpen(false)}
          width={40}
          isDirty={true}
          confirmOnClose={false}>
          <form onSubmit={handleSubmit(onSmtpSubmit)} className="space-y-4">
            <p className="text-xs text-gray-500">
              Defaults to smtp.gmail.com:465. Create an App Password in Google if 2FA is on.
            </p>
            <div className="grid grid-cols-1 gap-4">
              <FormInput
                label="SMTP User (Gmail)"
                register_key="smtp_user"
                register={register}
                errors={errors.smtp_user}
                isRequired={true}
                readOnly={isSavingSmtp}
                width={100}
              />
              <FormInput
                label="SMTP Password (App Password)"
                register_key="smtp_password"
                type="password"
                register={register}
                errors={errors.smtp_password}
                isRequired={true}
                readOnly={isSavingSmtp}
                width={100}
              />
              <FormInput
                label="From Email"
                register_key="smtp_from_email"
                register={register}
                errors={errors.smtp_from_email}
                isRequired={true}
                readOnly={isSavingSmtp}
                width={100}
              />
              <FormInput
                label="From Name"
                register_key="smtp_from_name"
                register={register}
                errors={errors.smtp_from_name}
                isRequired={true}
                readOnly={isSavingSmtp}
                width={100}
              />
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <Button
                type="button"
                displayType="secondary"
                disabled={isSavingSmtp}
                onClick={() => setSmtpModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSavingSmtp}>
                {isSavingSmtp ? 'Connecting…' : 'Connect'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
};

export default Profile;
